import { useDocumentTitle } from "@/lib/use-document-title";
import { useState } from "react";
import { CustomerLayout } from "@/components/CustomerLayout";
import { Copy, MessageSquare, Mail, CheckCircle2, Gift, Users } from "lucide-react";
import { useCurrentMember } from "@/lib/data-hooks";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { customerUrl } from "@/lib/public-urls";

function ReferralPage() {
  useDocumentTitle("Bjud in en vän — Lagamobilen Access");
  const member = useCurrentMember();
  const code = member?.referral_code ?? "";
  const link = code ? customerUrl(`/access/skapa-konto?ref=${code}`) : "";
  const [copied, setCopied] = useState(false);

  const { data: referrals = [] } = useQuery({
    queryKey: ["my_referrals", member?.id],
    enabled: !!member?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("id, status, referred_email, created_at, points_awarded")
        .eq("referrer_member_id", member!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const copy = () => {
    if (!link) return;
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CustomerLayout title="Bjud in en vän">
      <div className="max-w-3xl">
        <div className="card-soft p-8 mb-6 bg-gradient-to-br from-white to-muted">
          <Gift className="text-primary mb-3" size={36} />
          <h2 className="mb-3">Dela Lagamobilen Access med en vän</h2>
          <p className="text-muted-foreground mb-6">När din vän blir medlem får ni <strong className="text-foreground">båda en exklusiv välkomstgåva</strong> – ett premium-skärmskydd plus 1 månads medlemskap gratis.</p>

          <label className="label-field">Din personliga inbjudningslänk</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input readOnly value={link || "Laddar…"} className="input-field flex-1 font-mono text-xs" />
            <button onClick={copy} disabled={!link} className="btn-pill !py-2.5 inline-flex items-center gap-2 disabled:opacity-50">
              {copied ? <><CheckCircle2 size={16} /> Kopierad!</> : <><Copy size={16} /> Kopiera</>}
            </button>
          </div>
          {code && <p className="text-xs text-muted-foreground mt-3">Din kod: <span className="font-mono">{code}</span></p>}

          <div className="flex flex-wrap gap-3 mt-6">
            <a href={`sms:?body=${encodeURIComponent("Kolla in Lagamobilen Access! " + link)}`} className="btn-pill-outline inline-flex items-center gap-2"><MessageSquare size={16} /> Dela via SMS</a>
            <a href={`mailto:?subject=Bli medlem i Lagamobilen Access&body=${encodeURIComponent("Jag tycker du ska kolla in detta: " + link)}`} className="btn-pill-outline inline-flex items-center gap-2"><Mail size={16} /> Dela via e-post</a>
          </div>
        </div>

        <div className="card-soft p-6">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Users size={18} /> Dina inbjudningar ({referrals.length})</h3>
          {referrals.length === 0 ? (
            <div className="text-sm text-muted-foreground">Ingen vän har använt din länk ännu – dela den för att börja samla välkomstgåvor.</div>
          ) : (
            <ul className="divide-y divide-border">
              {referrals.map(r => (
                <li key={r.id} className="py-3 flex items-center justify-between gap-4 text-sm">
                  <div>
                    <div className="font-display font-semibold">{r.referred_email ?? "Anonym värvning"}</div>
                    <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}</div>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-display font-semibold ${
                    r.status === "completed" ? "bg-emerald-500/15 text-emerald-700" :
                    r.status === "pending" ? "bg-amber-500/15 text-amber-700" :
                    "bg-zinc-200 text-zinc-600"
                  }`}>
                    {r.status === "completed" ? "Aktiverad" : r.status === "pending" ? "Väntar" : r.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}

export default ReferralPage;
